// Seite „Lesezeichen" (/bookmarks): Ordner-Seitenleiste + Liste. Vertrag: docs/API.md 3.11.
import type { BookmarkDto, BookmarkFolderDto } from '@lagerfeuer/shared';
import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { bookmarksApi } from '../api';
import { formatDateTime } from '../format';
import { MessagePreview } from '../message-preview';
import { toast } from '../toast';
import { Button, ConfirmButton, Empty, ErrorBox, ExtrasPage, Field, Loading } from '../ui';
import { FolderColorPicker } from './FolderColorPicker';
import {
  BOOKMARK_FOLDERS_KEY,
  BOOKMARK_PAGE_SIZE,
  BOOKMARKS_KEY,
  bookmarkListKey,
  flattenBookmarks,
  FOLDER_NAME_MAX,
  oldestId,
  sortFolders,
  type BookmarkFilter,
} from './keys';
import './bookmarks.css';

export function BookmarksPage() {
  const [selected, setSelected] = useState<BookmarkFilter>('all');
  const foldersQuery = useQuery({ queryKey: BOOKMARK_FOLDERS_KEY, queryFn: bookmarksApi.folders });
  const folders = sortFolders(foldersQuery.data ?? []);
  const current = folders.find((f) => f.id === selected) ?? null;
  // Gelöschter/unbekannter Ordner → zurück auf „Alle".
  const filter: BookmarkFilter = selected !== 'all' && selected !== 'none' && foldersQuery.isSuccess && !current ? 'all' : selected;
  const title = filter === 'all' ? 'Alle Lesezeichen' : filter === 'none' ? 'Ohne Ordner' : (current?.name ?? 'Ordner');

  return (
    <ExtrasPage title="Lesezeichen" testId="bookmarks-page" wide>
      <div className="lfx-bm-layout">
        <FolderSidebar folders={folders} selected={filter} onSelect={setSelected} loading={foldersQuery.isPending} error={foldersQuery.error} />
        <section className="lfx-bm-main" aria-label={title}>
          <div className="lfx-row lfx-row--between lfx-bm-main__head">
            <h2 className="lfx-bm-main__title" data-testid="bookmarks-current-title">
              {current?.color && <span className="lfx-bm-dot" style={{ background: current.color }} aria-hidden="true" />}
              {title}
            </h2>
          </div>
          {current && filter === current.id && <FolderActions key={current.id} folder={current} folders={folders} onDeleted={() => setSelected('all')} />}
          <BookmarkList filter={filter} folders={folders} />
        </section>
      </div>
    </ExtrasPage>
  );
}

function FolderSidebar(props: { folders: BookmarkFolderDto[]; selected: BookmarkFilter; onSelect: (f: BookmarkFilter) => void; loading: boolean; error: unknown }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => bookmarksApi.createFolder({ name: name.trim(), ...(color ? { color } : {}) }),
    onSuccess: (f) => {
      void qc.invalidateQueries({ queryKey: BOOKMARK_FOLDERS_KEY });
      setAdding(false);
      setName('');
      setColor(null);
      props.onSelect(f.id);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };
  const entry = (key: BookmarkFilter, label: string, testId: string, extra?: { color?: string | null; count?: number }) => (
    <li key={key}>
      <button
        type="button"
        className={`lfx-bm-folder${props.selected === key ? ' lfx-bm-folder--active' : ''}`}
        aria-current={props.selected === key ? 'true' : undefined}
        data-testid={testId}
        data-id={key}
        onClick={() => props.onSelect(key)}
      >
        <span className="lfx-bm-dot" style={extra?.color ? { background: extra.color } : undefined} aria-hidden="true" />
        <span className="lfx-grow lfx-bm-folder__name">{label}</span>
        {extra?.count !== undefined && (
          <span className="lfx-bm-folder__count" aria-label={`${extra.count} Lesezeichen`}>
            {extra.count}
          </span>
        )}
      </button>
    </li>
  );
  return (
    <nav className="lfx-bm-sidebar" aria-label="Lesezeichen-Ordner" data-testid="bookmarks-sidebar">
      <ul className="lfx-bm-folders">
        {entry('all', 'Alle', 'bookmarks-folder-all')}
        {entry('none', 'Ohne Ordner', 'bookmarks-folder-none')}
        {props.folders.map((f) => entry(f.id, f.name, 'bookmarks-folder', { color: f.color, count: f.count }))}
      </ul>
      {props.loading && <Loading testId="bookmarks-folders-loading" />}
      <ErrorBox error={props.error} testId="bookmarks-folders-error" />
      {adding ? (
        <form className="lfx-stack lfx-bm-newfolder" onSubmit={submit} data-testid="bookmarks-folder-form">
          <Field label="Neuer Ordner">
            {(id) => (
              <input id={id} className="lfx-input" data-testid="bookmarks-folder-name" maxLength={FOLDER_NAME_MAX} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            )}
          </Field>
          <FolderColorPicker value={color} onChange={setColor} testId="bookmarks-folder-color" />
          <ErrorBox error={create.error} testId="bookmarks-folder-form-error" />
          <div className="lfx-row">
            <Button variant="primary" type="submit" testId="bookmarks-folder-create" disabled={!name.trim() || create.isPending}>
              Anlegen
            </Button>
            <Button testId="bookmarks-folder-create-cancel" onClick={() => setAdding(false)}>
              Abbrechen
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="ghost" testId="bookmarks-folder-add" onClick={() => setAdding(true)}>
          + Ordner anlegen
        </Button>
      )}
    </nav>
  );
}

function FolderActions({ folder, folders, onDeleted }: { folder: BookmarkFolderDto; folders: BookmarkFolderDto[]; onDeleted: () => void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const [color, setColor] = useState<string | null>(folder.color);
  const index = folders.findIndex((f) => f.id === folder.id);
  const invalidate = () => void qc.invalidateQueries({ queryKey: BOOKMARKS_KEY });
  const update = useMutation({
    mutationFn: (body: { name?: string; color?: string | null; position?: number }) => bookmarksApi.updateFolder(folder.id, body),
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });
  const remove = useMutation({
    mutationFn: () => bookmarksApi.deleteFolder(folder.id),
    onSuccess: () => {
      invalidate();
      toast({ kind: 'info', title: 'Ordner gelöscht', body: 'Die Lesezeichen sind jetzt ohne Ordner.' });
      onDeleted();
    },
  });
  // Verschieben: Position des Nachbarn übernehmen (Server ordnet die übrigen neu, Annahme – siehe Bericht).
  const move = (dir: -1 | 1) => {
    const neighbour = folders[index + dir];
    if (neighbour) update.mutate({ position: neighbour.position });
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const body: { name?: string; color?: string | null } = {};
    if (trimmed !== folder.name) body.name = trimmed;
    if (color !== folder.color) body.color = color;
    if (!Object.keys(body).length) return setEditing(false);
    update.mutate(body);
  };
  return (
    <div className="lfx-stack lfx-bm-folder-actions" data-testid="bookmarks-folder-actions">
      {editing ? (
        <form className="lfx-stack" onSubmit={submit} data-testid="bookmarks-folder-edit-form">
          <Field label="Ordnername">
            {(id) => <input id={id} className="lfx-input" data-testid="bookmarks-folder-edit-name" maxLength={FOLDER_NAME_MAX} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <FolderColorPicker value={color} onChange={setColor} testId="bookmarks-folder-edit-color" />
          <div className="lfx-row">
            <Button variant="primary" type="submit" testId="bookmarks-folder-edit-save" disabled={!name.trim() || update.isPending}>
              Speichern
            </Button>
            <Button testId="bookmarks-folder-edit-cancel" onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
          </div>
        </form>
      ) : (
        <div className="lfx-row">
          <Button
            testId="bookmarks-folder-edit"
            onClick={() => {
              setName(folder.name);
              setColor(folder.color);
              setEditing(true);
            }}
          >
            Umbenennen / Farbe
          </Button>
          <Button variant="ghost" testId="bookmarks-folder-up" disabled={index <= 0 || update.isPending} onClick={() => move(-1)} aria-label="Ordner nach oben">
            ↑ Nach oben
          </Button>
          <Button variant="ghost" testId="bookmarks-folder-down" disabled={index >= folders.length - 1 || update.isPending} onClick={() => move(1)} aria-label="Ordner nach unten">
            ↓ Nach unten
          </Button>
          <ConfirmButton label="Ordner löschen" confirmLabel="Wirklich löschen?" testId="bookmarks-folder-delete" disabled={remove.isPending} onConfirm={() => remove.mutate()} />
          <span className="lfx-muted">Beim Löschen bleiben die Lesezeichen erhalten (ohne Ordner).</span>
        </div>
      )}
      <ErrorBox error={update.error ?? remove.error} testId="bookmarks-folder-actions-error" />
    </div>
  );
}

function BookmarkList({ filter, folders }: { filter: BookmarkFilter; folders: BookmarkFolderDto[] }) {
  const list = useInfiniteQuery({
    queryKey: bookmarkListKey(filter),
    queryFn: ({ pageParam }) => bookmarksApi.list({ folderId: filter === 'all' ? undefined : filter, before: pageParam, limit: BOOKMARK_PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.length < BOOKMARK_PAGE_SIZE ? undefined : oldestId(last)),
  });
  if (list.isPending) return <Loading testId="bookmarks-loading" />;
  if (!list.data) return <ErrorBox error={list.error} testId="bookmarks-error" />;
  const items = flattenBookmarks(list.data.pages);
  if (!items.length) return <Empty testId="bookmarks-empty">Hier gibt es noch keine Lesezeichen. Setze eins über das Menü einer Nachricht.</Empty>;
  return (
    <>
      <ul className="lfx-bm-list" data-testid="bookmarks-list">
        {items.map((b) => (
          <BookmarkItem key={b.id} bookmark={b} folders={folders} />
        ))}
      </ul>
      <ErrorBox error={list.isFetchNextPageError ? list.error : null} testId="bookmarks-more-error" />
      {list.hasNextPage ? (
        <Button testId="bookmarks-load-more" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {list.isFetchingNextPage ? 'Lädt …' : 'Ältere laden'}
        </Button>
      ) : (
        <div className="lfx-muted lfx-bm-end" data-testid="bookmarks-end">
          Keine weiteren Lesezeichen.
        </div>
      )}
    </>
  );
}

function BookmarkItem({ bookmark: b, folders }: { bookmark: BookmarkDto; folders: BookmarkFolderDto[] }) {
  const qc = useQueryClient();
  const noteMax = FIXED_LIMITS.bookmarkNoteMax;
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(b.note ?? '');
  const invalidate = () => void qc.invalidateQueries({ queryKey: BOOKMARKS_KEY });
  const update = useMutation({
    mutationFn: (body: { folderId?: string | null; note?: string | null }) => bookmarksApi.update(b.id, body),
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });
  const remove = useMutation({ mutationFn: () => bookmarksApi.remove(b.id), onSuccess: invalidate });
  const tooLong = note.length > noteMax;
  const saveNote = (e: FormEvent) => {
    e.preventDefault();
    if (tooLong) return;
    update.mutate({ note: note.trim() ? note.trim() : null });
  };
  const folder = folders.find((f) => f.id === b.folderId);
  return (
    <li className="lfx-card lfx-bm-item" data-testid="bookmarks-item" data-id={b.id} style={folder?.color ? { borderLeftColor: folder.color } : undefined}>
      {b.message ? (
        <MessagePreview message={b.message} testId="bookmarks-item-message" />
      ) : (
        <div className="lfx-muted lfx-bm-missing" data-testid="bookmarks-item-missing">
          Nachricht nicht mehr verfügbar.
        </div>
      )}
      {editing ? (
        <form className="lfx-stack" onSubmit={saveNote} data-testid="bookmarks-note-form">
          <Field label="Notiz" error={tooLong ? `Höchstens ${noteMax} Zeichen.` : null} hint={<span className={tooLong ? 'lfx-bm-count lfx-bm-count--over' : 'lfx-bm-count'}>{`${note.length} / ${noteMax}`}</span>}>
            {(id) => <textarea id={id} className="lfx-textarea" data-testid="bookmarks-note-input" value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
          <div className="lfx-row">
            <Button variant="primary" type="submit" testId="bookmarks-note-save" disabled={tooLong || update.isPending}>
              Speichern
            </Button>
            <Button
              testId="bookmarks-note-cancel"
              onClick={() => {
                setNote(b.note ?? '');
                setEditing(false);
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      ) : (
        b.note && (
          <p className="lfx-bm-note" data-testid="bookmarks-item-note">
            {b.note}
          </p>
        )
      )}
      <ErrorBox error={update.error ?? remove.error} testId="bookmarks-item-error" />
      <div className="lfx-row">
        <span className="lfx-muted">Gespeichert {formatDateTime(b.createdAt)}</span>
        <span className="lfx-grow" />
        <label className="lfx-row lfx-bm-move">
          <span className="lfx-muted">Ordner</span>
          <select
            className="lfx-select"
            data-testid="bookmarks-item-folder"
            value={b.folderId ?? ''}
            disabled={update.isPending}
            onChange={(e) => update.mutate({ folderId: e.target.value || null })}
          >
            <option value="">Ohne Ordner</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        {!editing && (
          <Button variant="ghost" testId="bookmarks-item-edit-note" onClick={() => setEditing(true)}>
            {b.note ? 'Notiz bearbeiten' : 'Notiz hinzufügen'}
          </Button>
        )}
        <ConfirmButton label="Entfernen" confirmLabel="Wirklich entfernen?" testId="bookmarks-item-delete" disabled={remove.isPending} onConfirm={() => remove.mutate()} />
      </div>
    </li>
  );
}
